import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Button, Empty, Skeleton, Tag, Typography } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import MultiCard from "../../../Components/MultiCard";
import { openJobDrawer } from "../../../Redux/Slices/DialogSlice";
import { ApiStatics } from "../../../Utils/constants";
import { expireTokenHandler } from "../../../Utils/helpers";
import "./index.scss";

const PAGE_SIZE = 10;
const JOBS_URL = `${process.env.REACT_APP_API_BACKEND_URL}/${ApiStatics.jobs.jobs}`;
const STATUS_LABELS = {
  approved: "approved",
  pending: "under review",
  rejected: "rejected",
  archived: "archived",
};

const authHeaders = () => ({
  headers: { Authorization: "Bearer " + localStorage.getItem("token") },
});

// * The list endpoint only returns the table's columns, so each job's full
// * details are fetched the same way the job drawer fetches them. A job whose
// * details fail to load still shows, with what the list gave us. * //
const fetchPage = async (status, page) => {
  const filter = status === "archived" ? "archived=true" : `status=${status}`;
  const { data } = await axios.get(
    `${JOBS_URL}?sort=-createdAt&page=${page}&limit=${PAGE_SIZE}&${filter}`,
    authHeaders()
  );
  const rows = data.data.data;
  const details = await Promise.allSettled(
    rows.map((row) => axios.get(`${JOBS_URL}/${row.id}`, authHeaders()))
  );
  return {
    total: data.total,
    jobs: rows.map((row, index) =>
      details[index].status === "fulfilled"
        ? { ...row, ...details[index].value.data.data.data }
        : row
    ),
  };
};

// * Shown in each card's header. Opens the same drawer as the table's eye
// * icon, where the job can be edited. * //
const CardActions = ({ job }) => {
  const dispatch = useDispatch();
  return (
    <div className="jobs-feed-card-actions">
      <Tag color={job.stealthMode ? "purple" : "default"}>
        {job.stealthMode ? "Stealth: company hidden" : "Company visible"}
      </Tag>
      <Button
        icon={<EyeOutlined />}
        onClick={() => dispatch(openJobDrawer({ job }))}
      >
        Open
      </Button>
    </div>
  );
};

/*
 * Every job in the selected tab as the full job card, newest first, loaded a
 * page at a time as the admin scrolls - so all of them can be read through
 * without opening each one from the table.
 */
const JobsFeed = ({ status }) => {
  const navigate = useNavigate();
  const { jobDrawer } = useSelector((state) => state.DialogSlice);
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const sentinel = useRef(null);
  // * Bumped when the tab changes, so a page still in flight for the old tab
  // * is dropped instead of being appended to the new one. * //
  const generation = useRef(0);
  const progress = useRef({ loading: false, page: 0, loaded: 0, total: null });

  const loadNext = useCallback(async () => {
    const run = progress.current;
    if (run.loading || (run.total !== null && run.loaded >= run.total)) return;
    const gen = generation.current;
    run.loading = true;
    setLoading(true);
    setFailed(false);
    try {
      const page = await fetchPage(status, run.page + 1);
      if (gen !== generation.current) return;
      run.page += 1;
      run.loaded += page.jobs.length;
      // * An empty page means the count was stale; stop rather than ask forever. * //
      run.total = page.jobs.length ? page.total : run.loaded;
      setJobs((current) => [...current, ...page.jobs]);
      setTotal(run.total);
    } catch (error) {
      if (gen !== generation.current) return;
      setFailed(true);
      expireTokenHandler(error?.response?.data?.message, navigate);
    } finally {
      if (gen === generation.current) {
        run.loading = false;
        setLoading(false);
      }
    }
  }, [status, navigate]);

  // * Start again from the first page whenever the tab changes. * //
  useEffect(() => {
    generation.current += 1;
    progress.current = { loading: false, page: 0, loaded: 0, total: null };
    setJobs([]);
    setTotal(null);
    setFailed(false);
    setLoading(false);
    loadNext();
  }, [loadNext]);

  // * Load the next page before the admin reaches the bottom. Re-observed after
  // * every page, so a short list that leaves the sentinel on screen keeps
  // * loading until it fills the page or runs out. * //
  useEffect(() => {
    const node = sentinel.current;
    if (!node || !("IntersectionObserver" in window)) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && loadNext(),
      { rootMargin: "800px 0px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [loadNext, jobs.length]);

  // * A job edited in the drawer is re-read when the drawer closes, so its
  // * card shows the change without reloading the whole list. * //
  const openedId = useRef(null);
  useEffect(() => {
    if (jobDrawer.open) {
      openedId.current = jobDrawer.selectedJobDetails?.id || null;
      return;
    }
    const id = openedId.current;
    openedId.current = null;
    if (!id) return;
    axios
      .get(`${JOBS_URL}/${id}`, authHeaders())
      .then(({ data }) => {
        const fresh = data.data.data;
        setJobs((current) =>
          current.map((job) => (job.id === id ? { ...job, ...fresh } : job))
        );
      })
      .catch(() => {});
  }, [jobDrawer.open, jobDrawer.selectedJobDetails]);

  const label = STATUS_LABELS[status] || status;
  const hasMore = total === null || jobs.length < total;

  return (
    <div className="jobs-feed">
      {total !== 0 && (
        <Typography.Text className="jobs-feed-count" aria-live="polite">
          {total === null
            ? `Loading ${label} jobs…`
            : `Showing ${jobs.length} of ${total} ${label} jobs, newest first`}
        </Typography.Text>
      )}

      {jobs.map((job) => (
        <MultiCard
          key={job.id}
          {...job}
          isJob={true}
          HeaderAction={CardActions}
        />
      ))}

      {loading && (
        <div className="jobs-feed-loading">
          <Skeleton active paragraph={{ rows: 4 }} />
        </div>
      )}

      {!loading && failed && (
        <div className="jobs-feed-end">
          <Typography.Text>Couldn't load more jobs.</Typography.Text>
          <Button onClick={loadNext}>Try again</Button>
        </div>
      )}

      {!loading && !failed && total === 0 && (
        <Empty description={`No ${label} jobs`} />
      )}

      {!loading && !failed && hasMore && jobs.length > 0 && (
        <div className="jobs-feed-end">
          <Button onClick={loadNext}>Load more</Button>
        </div>
      )}

      {!loading && !failed && !hasMore && total > 0 && (
        <Typography.Text className="jobs-feed-end">
          That's all {total} {label} jobs.
        </Typography.Text>
      )}

      <div ref={sentinel} className="jobs-feed-sentinel" aria-hidden="true" />
    </div>
  );
};

export default JobsFeed;
